import { ViewPage, PageSchema } from '../types/pageTypes';
import {
  createDirectus,
  staticToken,
  rest,
  readItems,
  readItem,
  createItem,
  updateItem,
  deleteItem,
  uploadFiles,
  readFile,
  deleteFile,
  updateFile,
} from '@directus/sdk';

export interface PageOperationResult {
  success: boolean;
  message: string;
  data?: any;
}

export interface DirectusPageServiceConfig {
  /** 页面数据表名称 */
  collectionName?: string;
  /** API基础URL */
  baseUrl?: string;
}

export class DirectusPageService {
  private _directusApi: any;
  private _collectionName: string;
  private _baseUrl: string;
  private _token: string;

  constructor(
    directusApi: any,
    config: DirectusPageServiceConfig = {}
  ) {
    this._directusApi = directusApi;
    this._collectionName = config.collectionName || 'view_pages';
    // this._baseUrl = process.env.AGIMX_CORE_API_URL || 'http://localhost:8055';
    this._baseUrl = directusApi.url;
    this._token = directusApi.token;
  }

  /**
   * 创建新的Directus客户端实例 - 解决类型推断问题
   */
  private createDirectusClient() {
    // const apiUrl = this._baseUrl.endsWith('/') ? this._baseUrl : this._baseUrl + '/';
    const apiUrl = this._baseUrl;
    const ACCESS_TOKEN = this._token;
    
    return createDirectus(apiUrl).with(staticToken(ACCESS_TOKEN)).with(rest());
  }

  /**
   * 获取所有页面列表
   */
  async getAllPages(): Promise<PageOperationResult> {
    try {
      const directus = this.createDirectusClient();
      const result = await directus.request(
        readItems(this._collectionName, {
          sort: ['date_created']
        })
      );

      return {
        success: true,
        message: '获取页面列表成功',
        data: result || []
      };
    } catch (error) {
      console.error('获取页面列表失败:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : '获取页面列表失败'
      };
    }
  }

  /**
   * 根据路由获取页面信息
   */
  async getPageByRoute(route: string): Promise<PageOperationResult> {
    try {
      const directus = this.createDirectusClient();
      const result = await directus.request(
        readItem(this._collectionName, route)
      );

      return {
        success: true,
        message: '获取页面信息成功',
        data: result
      };
    } catch (error) {
      console.error('获取页面信息失败:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : '获取页面信息失败'
      };
    }
  }

  /**
   * 读取页面JSON配置
   */
  async getPageSchema(route: string): Promise<PageOperationResult> {
    try {
      const directus = this.createDirectusClient();
      
      // 先获取页面信息，包含json_file字段
      const pageResult = await directus.request(
        readItem(this._collectionName, route, {
          fields: ['*']
        })
      );
      // console.log("已调用接口拿到数据息:",pageResult);
      
      if (!pageResult?.json_file) {
        return {
          success: false,
          message: '页面未关联配置文件'
        };
      }

      // 直接构建文件下载URL
      const fileUrl = `${this._baseUrl}/assets/${pageResult.json_file}`;
    //   const response = await fetch(fileUrl);
      const response = await fetch(fileUrl, {
        headers: { 'Authorization': `Bearer ${this._token}` }
     });
    //  console.log('fetch fileUrl:',fileUrl,response);
     
      
      if (!response.ok) {
        throw new Error(`获取文件内容失败: ${response.status}`);
      }

      const schemaText = await response.text();
      const schema = JSON.parse(schemaText);
      // console.log("获取页面配置成功:",schema);
      
      return {
        success: true,
        message: '获取页面配置成功',
        data: schema
      };
    } catch (error) {
      console.error('获取页面配置失败:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : '获取页面配置失败'
      };
    }
  }

  /**
   * 创建新页面
   */
  async createPage(pageData: {
    route: string;
    name: string;
    icon?: string;
    schema?: PageSchema;
  }): Promise<PageOperationResult> {
    try {
      const directus = this.createDirectusClient();
      let jsonFileId: string | null = null;

      // 如果提供了schema，先上传文件
      if (pageData.schema) {
        const schemaBlob = new Blob([JSON.stringify(pageData.schema, null, 2)], {
          type: 'application/json'
        });
        
        const formData = new FormData();
        formData.append('file', schemaBlob, `${pageData.route}.json`);

        try {
          const fileResult = await directus.request(
            uploadFiles(formData)
          );
          jsonFileId = fileResult?.id || null;
        } catch (uploadError) {
          console.warn('文件上传失败，将创建无配置文件的页面:', uploadError);
        }
      }

      // 创建页面记录
      const newPage: Partial<ViewPage> = {
        route: pageData.route,
        name: pageData.name,
        icon: pageData.icon || 'fa fa-file',
        json_file: jsonFileId
      };

      const result = await directus.request(
        createItem(this._collectionName, newPage)
      );

      return {
        success: true,
        message: '创建页面成功',
        data: result
      };
    } catch (error) {
      console.error('创建页面失败:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : '创建页面失败'
      };
    }
  }

  /**
   * 更新页面信息
   */
  async updatePage(route: string, pageData: Partial<ViewPage>): Promise<PageOperationResult> {
    try {
      const directus = this.createDirectusClient();
      const result = await directus.request(
        updateItem(this._collectionName, route, pageData)
      );

      return {
        success: true,
        message: '更新页面信息成功',
        data: result
      };
    } catch (error) {
      console.error('更新页面信息失败:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : '更新页面信息失败'
      };
    }
  }

  /**
   * 更新页面配置
   */
  async updatePageSchema(route: string, schema: PageSchema): Promise<PageOperationResult> {
    try {
      const directus = this.createDirectusClient();
      
      // 获取当前页面信息
      // const pageResult = await this.getPageByRoute(route);
      // if (!pageResult.success) {
      //   return pageResult;
      // }

      // const page = pageResult.data;
      // console.log("这能直接获取？？？",page);
      
      // 创建新的JSON文件
      const schemaBlob = new Blob([JSON.stringify(schema, null, 2)], {
        type: 'application/json'
      });
      
      const formData = new FormData();
      formData.append('file', schemaBlob, `${route}.json`);

      let newFileId: string = '';

      const fileResult = await directus.request(
        uploadFiles(formData)
      );
      newFileId = fileResult.id;
      // 更新页面记录中的文件关联
      await this.updatePage(route, { json_file: newFileId });

      return {
        success: true,
        message: '更新页面配置成功',
        data: { fileId: newFileId }
      };
    } catch (error) {
      console.error('更新页面配置失败:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : '更新页面配置失败'
      };
    }
  }

  /**
   * 删除页面
   */
  async deletePage(route: string): Promise<PageOperationResult> {
    try {
      const directus = this.createDirectusClient();
      
      // 先获取页面信息，以便删除关联文件
      const pageResult = await this.getPageByRoute(route);
      
      if (pageResult.success && pageResult.data?.json_file) {
        // 删除关联的JSON文件
        try {
          await directus.request(
            deleteFile(pageResult.data.json_file)
          );
        } catch (fileError) {
          console.warn('删除关联文件失败:', fileError);
        }
      }

      // 删除页面记录
      await directus.request(
        deleteItem(this._collectionName, route)
      );

      return {
        success: true,
        message: '删除页面成功'
      };
    } catch (error) {
      console.error('删除页面失败:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : '删除页面失败'
      };
    }
  }

  /**
   * 根据筛选条件获取页面列表
   */
  async getPagesByFilter(filter: any = {}): Promise<PageOperationResult> {
    try {
      const directus = this.createDirectusClient();
      const result = await directus.request(
        readItems(this._collectionName, {
          filter,
          sort: ['date_created']
        })
      );

      return {
        success: true,
        message: '获取筛选页面列表成功',
        data: result || []
      };
    } catch (error) {
      console.error('获取筛选页面列表失败:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : '获取筛选页面列表失败'
      };
    }
  }

  /**
   * 批量删除页面
   */
  async deletePages(routes: string[]): Promise<PageOperationResult> {
    try {
      const directus = this.createDirectusClient();
      const deletedPages: string[] = [];
      const failedPages: string[] = [];

      for (const route of routes) {
        try {
          // 先获取页面信息，以便删除关联文件
          const pageResult = await this.getPageByRoute(route);
          
          if (pageResult.success && pageResult.data?.json_file) {
            // 删除关联的JSON文件
            try {
              await directus.request(
                deleteFile(pageResult.data.json_file)
              );
            } catch (fileError) {
              console.warn(`删除页面 ${route} 关联文件失败:`, fileError);
            }
          }

          // 删除页面记录
          await directus.request(
            deleteItem(this._collectionName, route)
          );

          deletedPages.push(route);
        } catch (error) {
          console.error(`删除页面 ${route} 失败:`, error);
          failedPages.push(route);
        }
      }

      return {
        success: failedPages.length === 0,
        message: `成功删除 ${deletedPages.length} 个页面${failedPages.length > 0 ? `，失败 ${failedPages.length} 个` : ''}`,
        data: { deleted: deletedPages, failed: failedPages }
      };
    } catch (error) {
      console.error('批量删除页面失败:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : '批量删除页面失败'
      };
    }
  }

  /**
   * 获取配置信息
   */
  getConfig(): DirectusPageServiceConfig {
    return {
      collectionName: this._collectionName,
      baseUrl: this._baseUrl
    };
  }

  /**
   * 更新配置信息
   */
  updateConfig(config: Partial<DirectusPageServiceConfig>): void {
    if (config.collectionName !== undefined) {
      this._collectionName = config.collectionName;
    }
    if (config.baseUrl !== undefined) {
      this._baseUrl = config.baseUrl;
    }
  }

  /**
   * 检查服务可用性
   */
  async checkHealth(): Promise<PageOperationResult> {
    try {
      const directus = this.createDirectusClient();
      // 尝试获取第一条记录来检查连接
      await directus.request(
        readItems(this._collectionName, { limit: 1 })
      );

      return {
        success: true,
        message: 'Directus页面服务连接正常'
      };
    } catch (error) {
      console.error('Directus页面服务连接失败:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Directus页面服务连接失败'
      };
    }
  }
}